import DEVICE_TYPE from "../../../enum/device_type";
import EVENT_TYPES from "../../../common/event-type";
import EVENT_CRUD_TYPES from "../../../common/event-crud-types";
import { writeAuditLog } from "../../../common/apis";
import { calculateTotalDueAmount } from "../helpers/orderUtils";

/**
 * Order service for managing order operations
 * Requires component reference to access state, socket, and methods
 */
class OrderService {
  constructor(component) {
    this.component = component;
  }

  /**
   * Fetch order details for multiple orders in a single batch request
   * @param {Array<string|number>} orderIds - Array of order IDs to fetch
   * @returns {Promise<{orders: Object[], errors: Array<{orderId: string|number, message: string}>, metadata: {totalReturned: number, totalErrors: number}}>}
   */
  async getOrderDetailsBatch(orderIds) {
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      return {
        orders: [],
        errors: [],
        metadata: { totalReturned: 0, totalErrors: 0 },
      };
    }

    const user = JSON.parse(sessionStorage.getItem("user"));
    const token =
      user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.jwtToken;

    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}deliveryconsole/getdetailsByID/batch`,
        {
          method: "POST",
          withCredentials: true,
          headers,
          body: JSON.stringify({ orderIds }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        orders: data.orders || [],
        errors: data.errors || [],
        metadata: data.metadata || {
          totalReturned: (data.orders || []).length,
          totalErrors: (data.errors || []).length,
        },
      };
    } catch (error) {
      console.error("Error fetching batch order details:", error);
      // Return empty result with error info
      return {
        orders: [],
        errors: orderIds.map((id) => ({ orderId: id, message: error.message })),
        metadata: { totalReturned: 0, totalErrors: orderIds.length },
      };
    }
  }

  /**
   * Refresh order details for multiple orders using batch API
   * Updates orderFullDetailsMap and optionally recalculates completion status
   * @param {Array<string|number>} orderIds - Array of order IDs to refresh
   * @param {boolean} updateCompletionStatus - Whether to recalculate completion status (default: true)
   * @returns {Promise<Object>} Updated orderFullDetailsMap
   */
  async refreshOrderDetailsBatch(orderIds, updateCompletionStatus = true) {
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      return this.component.state.orderFullDetailsMap || {};
    }

    const { orderFullDetailsMap, patientOrders, orderBagScanStatus } = this.component.state;
    const updatedFullDetailsMap = { ...orderFullDetailsMap };

    try {
      const batchResult = await this.getOrderDetailsBatch(orderIds);
      
      // Update the map with fetched data
      if (batchResult.orders && batchResult.orders.length > 0) {
        batchResult.orders.forEach((orderData) => {
          if (orderData && orderData.id) {
            updatedFullDetailsMap[orderData.id] = orderData;
          }
        });
      }

      // Log any errors
      if (batchResult.errors && batchResult.errors.length > 0) {
        batchResult.errors.forEach((err) => {
          console.error(`Error fetching order ${err.orderId}: ${err.message}`);
        });
      }

      // Update state
      const stateUpdate = { orderFullDetailsMap: updatedFullDetailsMap };

      // Recalculate completion status if requested
      if (updateCompletionStatus && patientOrders && patientOrders.length > 0) {
        const { checkOrderCompletion } = require("../helpers/completionUtils");
        const updatedCompletionStatus = {};
        
        patientOrders.forEach((order) => {
          if (!order || !order.id) return;
          const fullDetails = updatedFullDetailsMap[order.id] || null;
          const bagScanStatus = orderBagScanStatus[order.id] || null;
          updatedCompletionStatus[order.id] = checkOrderCompletion(
            order,
            fullDetails,
            patientOrders,
            updatedFullDetailsMap,
            bagScanStatus
          );
        });

        stateUpdate.orderCompletionStatus = updatedCompletionStatus;
      }

      this.component.setState(stateUpdate);
      return updatedFullDetailsMap;
    } catch (error) {
      console.error("Error in refreshOrderDetailsBatch:", error);
      return updatedFullDetailsMap;
    }
  }

  /**
   * Extract payment status from order data
   * @param {Object} orderData - Order data object
   * @returns {boolean} Payment status
   */
  extractPaymentStatus(orderData) {
    if (!orderData) return false;
    const amountData = orderData.amount;
    return (
      (amountData && typeof amountData.isPaid === "boolean" && amountData.isPaid) ||
      (amountData && typeof amountData.is_paid === "boolean" && amountData.is_paid) ||
      (orderData && typeof orderData.is_paid === "boolean" && orderData.is_paid) ||
      false
    );
  }

  /**
   * Move order to different status (complete, return, cancel, hold, release)
   * Uses batch API internally - works for both single and multiple orders
   * @param {string} type - Action type: "complete", "return", "cancel", "hold", or "release"
   * @param {string|number|Array} orderIdOverride - Optional order ID(s) - can be single ID or array of IDs
   * @returns {Promise<boolean>} Returns true if successful, false otherwise
   */
  async moveHandler(type, orderIdOverride = null) {
    this.component.setState({ loader: true });

    if (type !== "complete" && type !== "return" && type !== "cancel" && type !== "hold" && type !== "release") {
      this.component.setState({ loader: false });
      return false;
    }

    // Determine order ID(s) to process
    let orderIds = [];
    
    if (orderIdOverride !== null) {
      // If orderIdOverride is provided, use it (can be single ID or array)
      if (Array.isArray(orderIdOverride)) {
        orderIds = orderIdOverride.filter(id => id != null);
      } else {
        orderIds = [orderIdOverride];
      }
    } else {
      // Use current order from state
      const currentOrderId = this.component.state.id;
      if (currentOrderId) {
        orderIds = [currentOrderId];
      }
    }

    if (orderIds.length === 0) {
      this.component.setState({ loader: false });
      return false;
    }

    try {
      // Use batch API for both single and multiple orders
      const batchResult = await this.batchMoveHandler(orderIds, type);

      // Convert batch result to boolean for backward compatibility
      const success = batchResult.success && (batchResult.failed === 0);

      // Audit log for single order (maintains existing log format)
      if (orderIds.length === 1) {
        try {
          const orderId = orderIds[0];
          const finalOrderData = (this.component.state.orderFullDetailsMap && this.component.state.orderFullDetailsMap[orderId]) ||
                                 (this.component.state.patientOrders && this.component.state.patientOrders.find(o => o && o.id === orderId)) ||
                                 (this.component.state.popupData !== "" &&
                                  this.component.state.popupData !== undefined &&
                                  this.component.state.popupData !== "undefined")
                                 ? this.component.state.popupData
                                 : null;

          const pucode = (finalOrderData && (finalOrderData.pucode || finalOrderData.pickup_code)) 
            || this.component.state.pucode 
            || "";
          
          const event_type_name = type === "complete" ? EVENT_TYPES.COMPLETE :
                                 type === "return" ? EVENT_TYPES.RETURN :
                                 type === "cancel" ? EVENT_TYPES.CANCEL :
                                 type === "hold" ? EVENT_TYPES.RX_HOLD :
                                 EVENT_TYPES.STOCKED;
          const readableStatus = event_type_name.replace(/_/g, " ").toLowerCase();
          
          await writeAuditLog(
            `Counter order ${pucode || orderId || "unknown"} marked as ${readableStatus}.`,
            "Counter Operations"
          );
        } catch (auditError) {
          console.error("Failed to write audit log for status update:", auditError);
        }
      }

      this.component.setState({ loader: false });
      return success;
    } catch (error) {
      console.error("Error updating status:", error);
      this.component.setState({ loader: false });
      return false;
    }
  }

  /**
   * Batch update order status (complete, return, cancel, hold, release)
   * Can be called directly for detailed response, or use moveHandler() for simple boolean result
   * @param {Array<string|number>} orderIds - Array of order IDs to update
   * @param {string} type - Action type: "complete", "return", "cancel", "hold", or "release"
   * @returns {Promise<Object>} Returns {
   *   success: boolean,
   *   total_orders: number,
   *   processed: number,
   *   failed: number,
   *   emails_sent: number,
   *   successful_order_ids: string[],
   *   failed_order_ids: string[],
   *   errors: []
   * }
   */
  async batchMoveHandler(orderIds, type, options = {}) {
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      return {
        success: false,
        total_orders: 0,
        processed: 0,
        failed: 0,
        emails_sent: 0,
        successful_order_ids: [],
        failed_order_ids: [],
        errors: [{ message: "No order IDs provided" }],
      };
    }

    let object_id = "";
    let event_type_name = "";
    let type_id = "";

    if (type === "complete") {
      object_id = DEVICE_TYPE.Completed;
      event_type_name = EVENT_TYPES.COMPLETE;
      type_id = object_id;
    } else if (type === "return") {
      object_id = DEVICE_TYPE.Returned;
      event_type_name = EVENT_TYPES.RETURN;
      type_id = object_id;
    } else if (type === "cancel") {
      object_id = DEVICE_TYPE.Cancelled;
      event_type_name = EVENT_TYPES.CANCEL;
      type_id = object_id;
    } else if (type === "hold") {
      object_id = DEVICE_TYPE.Hold;
      event_type_name = EVENT_TYPES.RX_HOLD;
      type_id = object_id;
    } else if (type === "release") {
      object_id = DEVICE_TYPE.Stocked;
      event_type_name = EVENT_TYPES.STOCKED;
      type_id = object_id;
    } else {
      return {
        success: false,
        total_orders: orderIds.length,
        processed: 0,
        failed: orderIds.length,
        emails_sent: 0,
        successful_order_ids: [],
        failed_order_ids: orderIds,
        errors: [{ message: `Invalid type: ${type}` }],
      };
    }

    // Get common data from first order (all orders should have same box_id, location_type_id)
    const { patientOrders, orderFullDetailsMap, box_id, selectedTypeId } = this.component.state;
    
    // Get first order details for common fields
    const firstOrderId = orderIds[0];
    const firstOrder = (patientOrders || []).find((o) => o && o.id === firstOrderId);
    const firstOrderFullDetails = orderFullDetailsMap[firstOrderId] || firstOrder;

    const boxId = (firstOrderFullDetails && firstOrderFullDetails.box_id) || 
                  (firstOrder && firstOrder.box_id) || 
                  box_id || 
                  "";

    const locationTypeId = (firstOrderFullDetails && firstOrderFullDetails.locationtype_id) || 
                          (firstOrder && firstOrder.locationtype_id) || 
                          selectedTypeId || 
                          "";

    if (!boxId) {
      return {
        success: false,
        total_orders: orderIds.length,
        processed: 0,
        failed: orderIds.length,
        emails_sent: 0,
        successful_order_ids: [],
        failed_order_ids: orderIds,
        errors: [{ message: "Box ID is required for batch update" }],
      };
    }

    const user = JSON.parse(sessionStorage.getItem("user"));
    const token =
      user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.jwtToken;

    // Build batch request payload
    const batchPayload = {
      order_ids: orderIds,
      box_id: boxId,
      event_type_name: event_type_name,
      type_id: type_id,
      location_type_id: locationTypeId,
      location_notes: (firstOrderFullDetails && firstOrderFullDetails.present_location) || 
                      (firstOrder && firstOrder.present_location) || 
                      "",
      event_name: EVENT_CRUD_TYPES.INSERT,
      description: `Batch ${event_type_name} for ${orderIds.length} order(s)`,
    };

    if (type === "complete") {
      batchPayload.is_dispense_call = options.isDispenseCall === true;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        process.env.REACT_APP_BASEURL + `deliveryconsole/batch-updatestatus`,
        {
          method: "POST",
          withCredentials: true,
          headers,
          body: JSON.stringify(batchPayload),
        }
      );

      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        const errorMessage = (responseData && responseData.message) || `HTTP error! status: ${response.status}`;
        return {
          success: false,
          total_orders: orderIds.length,
          processed: 0,
          failed: orderIds.length,
          emails_sent: 0,
          successful_order_ids: [],
          failed_order_ids: orderIds,
          errors: [{ message: errorMessage }],
        };
      }

      // Handle API response structure
      // Expected: { success, total_orders, processed, failed, emails_sent, successful_order_ids, failed_order_ids }
      const apiSuccess = (responseData && responseData.success) || false;
      const totalOrders = (responseData && responseData.total_orders) || orderIds.length;
      const processed = (responseData && responseData.processed) || 0;
      const failed = (responseData && responseData.failed) || 0;
      const emailsSent = (responseData && responseData.emails_sent) || 0;
      const successfulOrderIds = (responseData && responseData.successful_order_ids) || [];
      const failedOrderIds = (responseData && responseData.failed_order_ids) || [];

      // Audit log
      try {
        const readableStatus = event_type_name.replace(/_/g, " ").toLowerCase();
        let auditMessage = `Batch ${readableStatus} for ${processed} counter order(s)`;
        if (failed > 0) {
          auditMessage += ` (${failed} failed)`;
        }
        if (emailsSent > 0) {
          auditMessage += `, ${emailsSent} email(s) sent`;
        }
        
        await writeAuditLog(auditMessage, "Counter Operations");
      } catch (auditError) {
        console.error("Failed to write audit log for batch status update:", auditError);
      }

      // Emit socket event to refresh console
      if (this.component.socket) {
        this.component.socket.emit("updateConsole");
      }

      return {
        success: apiSuccess && failed === 0,
        total_orders: totalOrders,
        processed: processed,
        failed: failed,
        emails_sent: emailsSent,
        successful_order_ids: successfulOrderIds,
        failed_order_ids: failedOrderIds,
        errors: failed > 0 ? [{ message: `${failed} order(s) failed to process`, failed_order_ids: failedOrderIds }] : [],
      };
    } catch (error) {
      console.error("Error in batch status update:", error);
      return {
        success: false,
        total_orders: orderIds.length,
        processed: 0,
        failed: orderIds.length,
        emails_sent: 0,
        successful_order_ids: [],
        failed_order_ids: orderIds,
        errors: [{ message: error.message || "Failed to update batch status" }],
      };
    }
  }

  /**
   * Send reminder notification
   */
  async reminderHandler() {
    this.component.setState({ open: false, loader: true });
    const { popupData } = this.component.state;
    const data = {
      order_id: popupData && popupData.id,
      account_id: popupData && popupData.parent_account_id,
      location_type_id: popupData && popupData.locationtype_id,
    };

    const user = JSON.parse(sessionStorage.getItem("user"));
    const token =
      user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.jwtToken;

    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    let shouldEmitUpdate = false;
    try {
      const response = await fetch(
        process.env.REACT_APP_BASEURL + "delivery/remindnotification",
        {
          method: "POST",
          withCredentials: true,
          headers,
          body: JSON.stringify(data),
        }
      );
      await response.json().catch(() => null);
      shouldEmitUpdate = true;

      const pickupCode =
        (popupData && (popupData.pucode || popupData.pickup_code)) || "";
      try {
        await writeAuditLog(
          `Reminder notification sent for counter order ${pickupCode || data.order_id || "unknown"}.`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error("Failed to write audit log for reminder notification:", auditError);
      }
    } catch (error) {
      // Error handled silently
    } finally {
      this.component.setState({ loader: false }, () => {
        if (shouldEmitUpdate && this.component.socket) {
          this.component.socket.emit("updateConsole");
        }
      });
    }
  }

  /**
   * Update order due amount
   */
  async updateOrderDueAmount(orderid, dueAmount) {
    this.component.setState({ loader: true });
    const updateParams = {
      orderid: orderid,
      dueamount: dueAmount,
    };

    const user = JSON.parse(sessionStorage.getItem("user"));
    const token =
      user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.jwtToken;

    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const { popupData } = this.component.state;
    const orderIdentifier =
      (popupData && (popupData.pucode || popupData.pickup_code)) ||
      (popupData && popupData.id) ||
      orderid;

    let shouldEmitUpdate = false;
    try {
      const response = await fetch(
        process.env.REACT_APP_BASEURL + "updateorderdueamout",
        {
          method: "PUT",
          withCredentials: true,
          headers,
          body: JSON.stringify(updateParams),
        }
      );
      await response.json().catch(() => null);
      shouldEmitUpdate = true;

      try {
        const parsedDueAmount = Number(dueAmount);
        const formattedDueAmount = Number.isFinite(parsedDueAmount)
          ? `$${parsedDueAmount.toFixed(2)}`
          : `${dueAmount}`;
        await writeAuditLog(
          `Due amount updated to ${formattedDueAmount} for counter order ${orderIdentifier || "unknown"}.`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error("Failed to write audit log for due amount update:", auditError);
      }
    } catch (error) {
      console.error("Error updating due amount:", error);
    } finally {
      this.component.setState({ loader: false }, () => {
        if (shouldEmitUpdate && this.component.socket) {
          this.component.socket.emit("updateConsole");
        }
      });
    }
  }

  /**
   * Refresh popup data from server using batch API
   */
  async refreshPopupData() {
    const { id } = this.component.state;
    if (!id) {
      return;
    }
    try {
      // Use batch API for consistency (works for single order too)
      const batchResult = await this.getOrderDetailsBatch([id]);
      
      if (!batchResult.orders || batchResult.orders.length === 0) {
        // Log error if fetch failed
        if (batchResult.errors && batchResult.errors.length > 0) {
          console.error(`Error refreshing order ${id}:`, batchResult.errors[0].message);
        }
        return;
      }

      const data = batchResult.orders[0];
      
      // Update consultation notes if available
      let updatedNotes = "";
      if (data && data.consultationDetails && Array.isArray(data.consultationDetails) && data.consultationDetails.length > 0) {
        const latestConsultation = data.consultationDetails[data.consultationDetails.length - 1];
        updatedNotes = latestConsultation.consultation_notes || "";
      }
      
      this.component.setState({ 
        popupData: data,
        consultationNotes: updatedNotes,
      }, () => {
        // Update completion status after refreshing popup data
        // Recalculate ALL patient orders so they all benefit from shared signature/ID
        if (id && data) {
          const { checkOrderCompletion } = require("../helpers/completionUtils");
          const patientOrders = this.component.state.patientOrders || [];
          const updatedFullDetailsMap = {
            ...this.component.state.orderFullDetailsMap,
            [id]: data,
          };
          
          // Update patientOrders array if this order is in it (for payment status sync)
          const updatedPatientOrders = patientOrders.map((order) => {
            if (!order || !order.id || order.id !== id) return order;
            
            const newIsPaid = this.extractPaymentStatus(data);
            const amountData = data.amount;
            
            return {
              ...order,
              is_paid: newIsPaid,
              amount: amountData ? { ...amountData, isPaid: newIsPaid, is_paid: newIsPaid } : order.amount,
            };
          });
          
          // Recalculate completion status for ALL patient orders
          const updatedCompletionStatus = {};
          updatedPatientOrders.forEach((order) => {
            if (!order || !order.id) return;
            const fullDetails = updatedFullDetailsMap[order.id] || null;
            const bagScanStatus = this.component.state.orderBagScanStatus[order.id] || null;
            updatedCompletionStatus[order.id] = checkOrderCompletion(
              order,
              fullDetails,
              updatedPatientOrders, // All patient orders for shared signature/ID
              updatedFullDetailsMap,
              bagScanStatus
            );
          });
          
          this.component.setState({
            orderCompletionStatus: updatedCompletionStatus,
            orderFullDetailsMap: updatedFullDetailsMap,
            patientOrders: updatedPatientOrders, // Update patientOrders with new payment status
          });
        }
      });
    } catch (error) {
      console.error("Error refreshing order details:", error);
    }
  }

  /**
   * Submit ID capture images
   * @param {string} frontImage - Base64 encoded front image
   * @param {string} backImage - Base64 encoded back image
   * @param {Array<number|string>} orderIds - Optional array of order IDs for batch operation
   */
  async handleSubmitIdCapture(frontImage, backImage, orderIds = null) {
    // If orderIds is provided (array), it's a batch operation from MultiPickupDialog
    if (Array.isArray(orderIds) && orderIds.length > 0) {
      await this.handleBatchIdCapture(frontImage, backImage, orderIds);
      return;
    }

    // Single order operation
    const { popupData } = this.component.state;
    const pickupCode =
      (popupData && (popupData.pucode || popupData.pickup_code)) || "";
    const orderId = (popupData && popupData.id) || "";
    const code = pickupCode || orderId;

    if (!code) {
      throw new Error("Unable to determine order code for ID capture.");
    }

    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      const token =
        user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken;
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const endpoint = `${process.env.REACT_APP_BASEURL}consultation/captured/images`;
      const payloads = [
        { code, image_url: frontImage, type: 0, replaceExisting: true },
        { code, image_url: backImage, type: 1, replaceExisting: true },
      ];

      for (const payload of payloads) {
        const response = await fetch(endpoint, {
          method: "POST",
          withCredentials: true,
          headers,
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          let errorMessage = "Failed to save captured ID image.";
          try {
            const errorBody = await response.json();
            if (errorBody && errorBody.message) {
              errorMessage = errorBody.message;
            }
          } catch (jsonError) {
            // Ignore JSON parsing error and use default message.
          }
          throw new Error(errorMessage);
        }
      }

      if (this.component.socket) {
        this.component.socket.emit("updateConsole");
      }
      const hadExistingIdCapture =
        popupData &&
        Array.isArray(popupData.capturedImages) &&
        popupData.capturedImages.some((image) => image && image.image_url);
      const captureSegments = [];
      if (frontImage) {
        captureSegments.push("front");
      }
      if (backImage) {
        captureSegments.push("back");
      }
      try {
        const captureStatus = hadExistingIdCapture ? "re-captured" : "captured";
        const captureDetails =
          captureSegments.length > 0
            ? ` (${captureSegments.join(" & ")})`
            : "";
        await writeAuditLog(
          `ID ${captureStatus}${captureDetails} for counter order ${code}.`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error("Failed to write audit log for ID capture:", auditError);
      }
      await this.refreshPopupData();
    } catch (error) {
      console.error("Error submitting ID capture:", error);
      throw error;
    }
  }

  /**
   * Batch ID capture for multiple orders
   * @param {string} frontImage - Base64 encoded front image
   * @param {string} backImage - Base64 encoded back image
   * @param {Array<number|string>} orderIds - Array of order IDs to capture ID for
   */
  async handleBatchIdCapture(frontImage, backImage, orderIds) {
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      throw new Error("No orders provided for ID capture.");
    }

    const { patientOrders, orderFullDetailsMap } = this.component.state;
    
    if (!patientOrders || patientOrders.length === 0) {
      throw new Error("Order data is missing.");
    }

    // Collect pickup codes for all orders
    const pickupCodes = [];
    const orderCodeMap = new Map(); // Map pickup code to order ID for error tracking

    for (const orderId of orderIds) {
      const order = (patientOrders || []).find((o) => o && o.id === orderId);
      if (!order) continue;

      const fullDetails = orderFullDetailsMap[orderId] || order;
      const pickupCode = fullDetails.pucode || fullDetails.pickup_code || order.pucode || order.pickup_code;
      
      if (pickupCode) {
        pickupCodes.push(pickupCode);
        orderCodeMap.set(pickupCode, orderId);
      }
    }

    if (pickupCodes.length === 0) {
      throw new Error("No valid pickup codes found for the selected orders.");
    }

    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      const token =
        user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken;
      
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // Use batch endpoint for captured images
      const batchEndpoint = `${process.env.REACT_APP_BASEURL}consultation/captured/images/batch`;
      
      // Make 2 API calls: one for front (type: 0), one for back (type: 1)
      const batchPayloads = [];
      
      if (frontImage) {
        batchPayloads.push({
          codes: pickupCodes,
          type: 0,
          image_url: frontImage,
          replaceExisting: true,
        });
      }
      
      if (backImage) {
        batchPayloads.push({
          codes: pickupCodes,
          type: 1,
          image_url: backImage,
          replaceExisting: true,
        });
      }

      // Process both front and back images
      let hasSuccessfulResults = false;
      const errors = [];

      for (const payload of batchPayloads) {
        try {
          const response = await fetch(batchEndpoint, {
            method: "POST",
            withCredentials: true,
            headers,
            body: JSON.stringify(payload),
          });

          const responseData = await response.json();

          if (!response.ok) {
            let errorMessage = "Failed to save captured ID image.";
            try {
              if (responseData && responseData.message) {
                errorMessage = responseData.message;
              }
            } catch (jsonError) {
              // Ignore parsing errors
            }
            throw new Error(errorMessage);
          }

          // Check for partial success or errors in response
          if (responseData && responseData.data) {
            if (responseData.data.errors && responseData.data.errors.invalid_prescriptions) {
              const invalidCodes = responseData.data.errors.invalid_prescriptions || [];
              invalidCodes.forEach(({ code, error }) => {
                errors.push({
                  pickupCode: code,
                  orderId: orderCodeMap.get(code),
                  error: error || "Prescription not found",
                });
              });
            }

            if (responseData.data.success && responseData.data.success.records) {
              hasSuccessfulResults = true;
            }
          }
        } catch (error) {
          console.error(`Error saving ${payload.type === 0 ? 'front' : 'back'} ID image in batch:`, error);
          // Collect errors but continue with other image
          errors.push({
            type: payload.type === 0 ? 'front' : 'back',
            error: error.message || "Failed to save image",
          });
        }
      }

      // If both front and back failed, throw error
      if (!hasSuccessfulResults && errors.length > 0) {
        const errorMessages = errors.map(e => e.error || "Unknown error").join("; ");
        throw new Error(`Batch ID capture failed: ${errorMessages}`);
      }

      // Refresh popup data for all updated orders using batch API
      await this.refreshOrderDetailsBatch(orderIds, true);

      if (this.component.socket) {
        this.component.socket.emit("updateConsole");
      }

      // Audit log
      const captureSegments = [];
      if (frontImage) {
        captureSegments.push("front");
      }
      if (backImage) {
        captureSegments.push("back");
      }
      try {
        const captureDetails =
          captureSegments.length > 0
            ? ` (${captureSegments.join(" & ")})`
            : "";
        const codesStr = pickupCodes.join(", ");
        await writeAuditLog(
          `Batch ID captured${captureDetails} for counter orders [${codesStr}].`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error("Failed to write audit log for batch ID capture:", auditError);
      }
    } catch (error) {
      console.error("Error in batch ID capture:", error);
      throw error;
    }
  }

  /**
   * Submit signature capture
   * @param {string} signatureData - Base64 encoded signature image
   * @param {Array<number|string>} orderIds - Optional array of order IDs for batch operation
   */
  async handleSubmitSignature(signatureData, orderIds = null) {
    // If orderIds is provided (array), it's a batch operation from MultiPickupDialog
    if (Array.isArray(orderIds) && orderIds.length > 0) {
      await this.handleBatchSubmitSignature(signatureData, orderIds);
      return;
    }

    // Single order operation
    const { popupData } = this.component.state;
    if (!signatureData) {
      throw new Error("Signature data is missing.");
    }

    const orderData =
      (popupData && popupData.res && popupData.res.order) ||
      (popupData && popupData.order) ||
      popupData ||
      {};
    const customer =
      (orderData && orderData.customer) ||
      (popupData && popupData.customer) ||
      {};
    const account =
      (popupData && popupData.res && popupData.res.account) ||
      (popupData && popupData.account) ||
      {};
    const box =
      (popupData && popupData.res && popupData.res.box) ||
      (popupData && popupData.box) ||
      {};

    const customerId = customer.id || (popupData && popupData.customer_id);
    const orderId = orderData.id || (popupData && popupData.id);

    if (!customerId || !orderId) {
      throw new Error("Unable to determine order details for signature capture.");
    }

    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      const token =
        user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken;
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const endpoint = `${process.env.REACT_APP_BASEURL}lifeboat/save/patient/details`;
      const firstName =
        customer.first_name || (popupData && popupData.first_name) || "";
      const lastName =
        customer.last_name || (popupData && popupData.last_name) || "";

      const customerData = {
        customer_id: customerId,
        orderIds: JSON.stringify([{ id: orderId }]),
        signature_url: signatureData,
        first_name: firstName,
        last_name: lastName,
        name: `${firstName} ${lastName}`.trim(),
        dob: customer.dob_str || customer.dob || "",
        relationship: "Patient",
        box_id: box.id || (popupData && popupData.box_id) || "",
        account_id:
          account.id || (popupData && popupData.parent_account_id) || "",
        dlrawdata: "",
        drivers_license_no: "",
        is_consult_requested: false,
        kioskBoxAccountConnection: "",
        middle_name: customer.middle_name || "",
        picture_url: "",
        selectedstatecode: "",
      };

      const response = await fetch(endpoint, {
        method: "POST",
        withCredentials: true,
        headers,
        body: JSON.stringify(customerData),
      });
      if (!response.ok) {
        let errorMessage = "Failed to save signature.";
        try {
          const errorBody = await response.json();
          if (errorBody && errorBody.message) {
            errorMessage = errorBody.message;
          }
        } catch (jsonError) {
          // Ignore parsing error and use default message.
        }
        throw new Error(errorMessage);
      }

      if (this.component.socket) {
        this.component.socket.emit("updateConsole");
      }
      try {
        const hadExistingSignature = Boolean(
          (popupData && popupData.signature_url) ||
            (orderData && orderData.signature_url)
        );
        const pickupCode =
          (popupData && (popupData.pucode || popupData.pickup_code)) || "";
        const signatureStatus = hadExistingSignature ? "re-captured" : "captured";
        await writeAuditLog(
          `Signature ${signatureStatus} for counter order ${pickupCode || orderId}.`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error("Failed to write audit log for signature capture:", auditError);
      }
      await this.refreshPopupData();
    } catch (error) {
      console.error("Error submitting signature:", error);
      throw error;
    }
  }

  /**
   * Batch submit signature for multiple orders
   * @param {string} signatureData - Base64 encoded signature image
   * @param {Array<number|string>} orderIds - Array of order IDs to save signature for
   */
  async handleBatchSubmitSignature(signatureData, orderIds) {
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      throw new Error("No orders provided for signature capture.");
    }

    const { patientOrders, orderFullDetailsMap } = this.component.state;
    
    if (!patientOrders || patientOrders.length === 0) {
      throw new Error("Order data is missing.");
    }

    // Get common data from first order
    const firstOrder = (patientOrders || []).find((o) => o && o.id === orderIds[0]);
    if (!firstOrder) {
      throw new Error("First order not found.");
    }

    const fullDetails = orderFullDetailsMap[firstOrder.id] || firstOrder;
    const customer = fullDetails.customer || {};
    const account = fullDetails.account || fullDetails.accDetails || {};
    const box = fullDetails.box || fullDetails.boxDetails || {};

    const customerId = fullDetails.customer_id || customer.id || null;
    const boxId = fullDetails.box_id || box.id || null;
    const accountId = fullDetails.parent_account_id || account.id || null;

    if (!customerId || !boxId || !accountId) {
      throw new Error("Missing required order information for signature capture.");
    }

    // Prepare orderIds array in the format expected by API
    const orderIdsArray = orderIds.map(id => ({ id }));

    const firstName = customer.first_name || fullDetails.first_name || "";
    const lastName = customer.last_name || fullDetails.last_name || "";

    const customerData = {
      customer_id: customerId,
      orderIds: JSON.stringify(orderIdsArray),  // Array of order IDs
      signature_url: signatureData,
      first_name: firstName,
      last_name: lastName,
      name: `${firstName} ${lastName}`.trim(),
      dob: customer.dob_str || customer.dob || "",
      relationship: "Patient",
      box_id: boxId,
      account_id: accountId,
      dlrawdata: "",
      drivers_license_no: "",
      is_consult_requested: false,
      kioskBoxAccountConnection: account.accountConnectionsId || null,
      middle_name: customer.middle_name || "",
      picture_url: "",
      selectedstatecode: "",
    };

    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      const token =
        user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken;
      
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const endpoint = `${process.env.REACT_APP_BASEURL}lifeboat/save/patient/details`;
      const response = await fetch(endpoint, {
        method: "POST",
        withCredentials: true,
        headers,
        body: JSON.stringify(customerData),
      });

      if (!response.ok) {
        let errorMessage = "Failed to save signature.";
        try {
          const errorBody = await response.json();
          if (errorBody && errorBody.message) {
            errorMessage = errorBody.message;
          }
        } catch (jsonError) {
          // Ignore parsing error
        }
        throw new Error(errorMessage);
      }

      if (this.component.socket) {
        this.component.socket.emit("updateConsole");
      }

      // Audit log
      const orderIdsStr = orderIds.join(", ");
      try {
        await writeAuditLog(
          `Batch signature captured for counter orders [${orderIdsStr}].`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error("Failed to write audit log for batch signature:", auditError);
      }

      // Refresh popup data for all updated orders using batch API
      await this.refreshOrderDetailsBatch(orderIds, true);
    } catch (error) {
      console.error("Error submitting batch signature:", error);
      throw error;
    }
  }

  /**
   * Toggle payment status
   */
  async handleTogglePaymentStatus() {
    const { popupData, isUpdatingPaymentStatus } = this.component.state;

    if (!popupData || isUpdatingPaymentStatus) {
      return;
    }

    const orderId =
      (popupData && popupData.id) ||
      (popupData && popupData.order_id) ||
      null;

    if (!orderId) {
      this.component.setState({
        paymentStatusError: this.component.props.t
          ? this.component.props.t("Console.ActionPopup.PaymentToggleMissingOrder", {
              defaultValue: "Unable to determine order for payment update.",
            })
          : "Unable to determine order for payment update.",
      });
      return;
    }

    const currentAmount = calculateTotalDueAmount(popupData);
    const approvedAmount = Number(currentAmount.toFixed(2));
    const amountData = popupData && popupData.amount;
    const currentPaidStatus =
      amountData && typeof amountData.isPaid === "boolean"
        ? amountData.isPaid
        : amountData && typeof amountData.is_paid === "boolean"
          ? amountData.is_paid
          : popupData && typeof popupData.is_paid === "boolean"
            ? popupData.is_paid
            : false;

    const nextPaidStatus = !currentPaidStatus;

    this.component.setState({
      isUpdatingPaymentStatus: true,
      paymentStatusError: "",
    });

    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      const token =
        user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken;

      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}payments/manual-status`,
        {
          method: "POST",
          withCredentials: true,
          headers,
          body: JSON.stringify({
            order_ids: [orderId], // Always use array
            is_paid: nextPaidStatus,
            approved_amt: approvedAmount,
          }),
        }
      );

      if (!response.ok) {
        let errorMessage = this.component.props.t
          ? this.component.props.t("Console.ActionPopup.PaymentToggleError", {
              defaultValue: "Failed to update payment status. Please try again.",
            })
          : "Failed to update payment status. Please try again.";

        try {
          const errorBody = await response.json();
          if (errorBody && errorBody.message) {
            errorMessage = errorBody.message;
          }
        } catch (parseError) {
          // ignore parsing issues
        }

        throw new Error(errorMessage);
      }

      try {
        const pickupCode =
          (popupData && (popupData.pucode || popupData.pickup_code)) || "";
        await writeAuditLog(
          `Payment status for counter order ${pickupCode || orderId} set to ${nextPaidStatus ? "paid" : "unpaid"} (approved amount ${approvedAmount.toFixed(2)}).`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error("Failed to write audit log for payment status update:", auditError);
      }

      await this.refreshPopupData();
      if (this.component.socket) {
        this.component.socket.emit("updateConsole");
      }
    } catch (error) {
      console.error("Error toggling payment status:", error);
      this.component.setState({
        paymentStatusError:
          (error && error.message) ||
          "Failed to update payment status. Please try again.",
      });
    } finally {
      this.component.setState({ isUpdatingPaymentStatus: false });
    }
  }

  /**
   * Batch toggle payment status for multiple orders
   * @param {Array<number|string>} orderIds - Array of order IDs to update
   */
  async handleBatchTogglePaymentStatus(orderIds) {
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      this.component.setState({
        paymentStatusError: "No orders selected for payment update.",
      });
      return;
    }

    const { orderFullDetailsMap, patientOrders, isUpdatingPaymentStatus } = this.component.state;

    if (isUpdatingPaymentStatus) {
      return;
    }

    // Calculate total amount - only include unpaid orders if there's a mix
    let totalApprovedAmount = 0;
    const ordersToUpdate = [];

    for (const orderId of orderIds) {
      const order = (patientOrders || []).find((o) => o && o.id === orderId);
      if (!order) continue;

      const fullDetails = orderFullDetailsMap[orderId] || order;
      const orderAmount = calculateTotalDueAmount(fullDetails);

      // Determine current paid status
      const amountData = fullDetails.amount;
      const currentPaidStatus =
        (amountData && typeof amountData.isPaid === "boolean" && amountData.isPaid) ||
        (amountData && typeof amountData.is_paid === "boolean" && amountData.is_paid) ||
        (fullDetails && typeof fullDetails.is_paid === "boolean" && fullDetails.is_paid) ||
        false;

      ordersToUpdate.push({
        orderId,
        currentPaidStatus,
        amount: orderAmount,
      });
    }

    if (ordersToUpdate.length === 0) {
      this.component.setState({
        paymentStatusError: "No valid orders found for payment update.",
      });
      return;
    }

    // Determine next paid status (toggle based on first order, or set to true if any is unpaid)
    const hasUnpaidOrder = ordersToUpdate.some((o) => !o.currentPaidStatus);
    const hasPaidOrder = ordersToUpdate.some((o) => o.currentPaidStatus);
    const nextPaidStatus = hasUnpaidOrder; // If any is unpaid, mark all as paid; otherwise mark all as unpaid

    // Calculate amount: if mixing paid and unpaid, only show unpaid amount; otherwise show all
    if (hasUnpaidOrder && hasPaidOrder) {
      // Mix of paid and unpaid - only include unpaid orders
      totalApprovedAmount = ordersToUpdate
        .filter((o) => !o.currentPaidStatus)
        .reduce((sum, o) => sum + o.amount, 0);
    } else {
      // All paid or all unpaid - include all orders
      totalApprovedAmount = ordersToUpdate.reduce((sum, o) => sum + o.amount, 0);
    }

    this.component.setState({
      isUpdatingPaymentStatus: true,
      paymentStatusError: "",
    });

    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      const token =
        user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken;

      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}payments/manual-status`,
        {
          method: "POST",
          withCredentials: true,
          headers,
          body: JSON.stringify({
            order_ids: orderIds, // Array of order IDs
            is_paid: nextPaidStatus,
            approved_amt: Number(totalApprovedAmount.toFixed(2)),
          }),
        }
      );

      if (!response.ok) {
        let errorMessage = "Failed to update payment status. Please try again.";
        try {
          const errorBody = await response.json();
          if (errorBody && errorBody.message) {
            errorMessage = errorBody.message;
          }
        } catch (parseError) {
          // ignore parsing issues
        }
        throw new Error(errorMessage);
      }

      // Refresh popup data for all updated orders using batch API
      const updatedFullDetailsMap = await this.refreshOrderDetailsBatch(orderIds, false);

      // Update patientOrders array with new payment status from refreshed data
      const updatedPatientOrders = patientOrders.map((order) => {
        if (!order || !order.id || !orderIds.includes(order.id)) return order;
        
        const fullDetails = updatedFullDetailsMap[order.id];
        if (!fullDetails) return order;
        
        const newIsPaid = this.extractPaymentStatus(fullDetails);
        const amountData = fullDetails.amount;
        
        return {
          ...order,
          is_paid: newIsPaid,
          amount: amountData ? { ...amountData, isPaid: newIsPaid, is_paid: newIsPaid } : order.amount,
        };
      });

      // Recalculate completion status for all patient orders
      const { checkOrderCompletion } = require("../helpers/completionUtils");
      const updatedCompletionStatus = {};
      updatedPatientOrders.forEach((order) => {
        if (!order || !order.id) return;
        const fullDetails = updatedFullDetailsMap[order.id] || null;
        const bagScanStatus = this.component.state.orderBagScanStatus[order.id] || null;
        updatedCompletionStatus[order.id] = checkOrderCompletion(
          order,
          fullDetails,
          updatedPatientOrders,
          updatedFullDetailsMap,
          bagScanStatus
        );
      });

      this.component.setState({
        orderCompletionStatus: updatedCompletionStatus,
        patientOrders: updatedPatientOrders, // Update patientOrders with new payment status
      });

      try {
        const orderIdsStr = orderIds.join(", ");
        await writeAuditLog(
          `Batch payment status for orders [${orderIdsStr}] set to ${nextPaidStatus ? "paid" : "unpaid"} (total approved amount ${totalApprovedAmount.toFixed(2)}).`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error("Failed to write audit log for batch payment status update:", auditError);
      }

      if (this.component.socket) {
        this.component.socket.emit("updateConsole");
      }
    } catch (error) {
      console.error("Error toggling batch payment status:", error);
      this.component.setState({
        paymentStatusError:
          (error && error.message) ||
          "Failed to update payment status. Please try again.",
      });
    } finally {
      this.component.setState({ isUpdatingPaymentStatus: false });
    }
  }

  /**
   * Save manual consultation with notes
   */
  async saveConsultation(consultationNotes) {
    const { popupData } = this.component.state;
    if (!popupData) {
      throw new Error("Order data is missing.");
    }

    const user = JSON.parse(sessionStorage.getItem("user"));
    if (!user || !user.username) {
      throw new Error("User information is missing.");
    }

    // Get user details
    let userId = null;
    let userName = "";
    try {
      const userResponse = await fetch(
        `${process.env.REACT_APP_BASEURL}users/${user.username}`,
        {
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const userData = await userResponse.text();
      if (userData) {
        const parsedUserData = JSON.parse(userData);
        if (parsedUserData && parsedUserData.length > 0) {
          userId = parsedUserData[0].id;
          userName = parsedUserData[0].name || user.username;
        }
      }
    } catch (error) {
      console.warn("Failed to fetch user details, using defaults:", error);
      userName = user.username;
    }

    const customer = popupData.customer || {};
    const account = popupData.account || popupData.accDetails || {};
    const box = popupData.box || popupData.boxDetails || {};

    const patientName =
      popupData.customerName ||
      `${popupData.first_name || ""} ${popupData.last_name || ""}`.trim() ||
      `${customer.first_name || ""} ${customer.last_name || ""}`.trim() ||
      "Patient";

    const consultationData = {
      consult_start_date: new Date(),
      consult_end_date: new Date(),
      pickup_code: popupData.pucode || popupData.pickup_code || "",
      user_name: patientName,
      call_duration: "00:00:00",
      user_id: userId,
      consultation_name: userName,
      Status: "Completed",
      accounts_id: popupData.parent_account_id || account.id || "",
      store_name: popupData.accDetails && popupData.accDetails.name,
      box_id: popupData.box_id || box.id || "",
      kiosk_name: popupData.boxDetails && popupData.boxDetails.name,
      consultation_notes: consultationNotes || "",
      is_releaseorder: true,
    };

    const token =
      user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.jwtToken;

    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}saveuserconsult`,
        {
          method: "POST",
          withCredentials: true,
          headers,
          body: JSON.stringify(consultationData),
        }
      );

      if (!response.ok) {
        let errorMessage = "Failed to save consultation.";
        try {
          const errorBody = await response.json();
          if (errorBody && errorBody.message) {
            errorMessage = errorBody.message;
          }
        } catch (parseError) {
          // Ignore parsing issues
        }
        throw new Error(errorMessage);
      }

      const pickupCode =
        popupData.pucode || popupData.pickup_code || popupData.id || "";
      try {
        await writeAuditLog(
          `Manual consultation completed for counter order ${pickupCode}.`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error(
          "Failed to write audit log for consultation:",
          auditError
        );
      }

      // Refresh popup data to get updated consultation status
      await this.refreshPopupData();
      if (this.component.socket) {
        this.component.socket.emit("updateConsole");
      }
    } catch (error) {
      console.error("Error saving consultation:", error);
      throw error;
    }
  }

  /**
   * Batch save consultation for multiple orders
   * @param {string} consultationNotes - Consultation notes
   * @param {Array<number|string>} orderIds - Array of order IDs to save consultation for
   */
  async saveBatchConsultation(consultationNotes, orderIds) {
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      throw new Error("No orders provided for consultation.");
    }

    const { orderFullDetailsMap, patientOrders } = this.component.state;

    if (!patientOrders || patientOrders.length === 0) {
      throw new Error("Order data is missing.");
    }

    const user = JSON.parse(sessionStorage.getItem("user"));
    if (!user || !user.username) {
      throw new Error("User information is missing.");
    }

    // Get user details
    let userId = null;
    let userName = "";
    try {
      const userResponse = await fetch(
        `${process.env.REACT_APP_BASEURL}users/${user.username}`,
        {
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const userData = await userResponse.text();
      if (userData) {
        const parsedUserData = JSON.parse(userData);
        if (parsedUserData && parsedUserData.length > 0) {
          userId = parsedUserData[0].id;
          userName = parsedUserData[0].name || user.username;
        }
      }
    } catch (error) {
      console.warn("Failed to fetch user details, using defaults:", error);
      userName = user.username;
    }

    // Get all orders and their pucodes (include all orders, not just those requiring consultation)
    // This allows saving notes even when consultation is not required
    const ordersToUpdate = [];
    const pickupCodes = [];

    for (const orderId of orderIds) {
      const order = (patientOrders || []).find((o) => o && o.id === orderId);
      if (!order) continue;

      const fullDetails = orderFullDetailsMap[orderId] || order;
      const pucode = fullDetails.pucode || fullDetails.pickup_code || order.pucode || order.pickup_code;
      
      if (pucode) {
        ordersToUpdate.push({ orderId, fullDetails, pucode });
        pickupCodes.push(pucode);
      }
    }

    if (pickupCodes.length === 0) {
      throw new Error("No valid orders found for consultation notes.");
    }

    // Get common data from first order
    const firstOrder = ordersToUpdate[0];
    const fullDetails = firstOrder.fullDetails;
    const customer = fullDetails.customer || {};
    const account = fullDetails.account || fullDetails.accDetails || {};
    const box = fullDetails.box || fullDetails.boxDetails || {};

    const patientName =
      fullDetails.customerName ||
      `${fullDetails.first_name || ""} ${fullDetails.last_name || ""}`.trim() ||
      `${customer.first_name || ""} ${customer.last_name || ""}`.trim() ||
      "Patient";

    const token =
      user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.jwtToken;

    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      // Prepare consultation data with array of pickup codes
      const consultationData = {
        consult_start_date: new Date(),
        consult_end_date: new Date(),
        pickup_code: pickupCodes, // Array of pickup codes
        user_name: patientName,
        call_duration: "00:00:00",
        user_id: userId,
        consultation_name: userName,
        Status: "Completed",
        accounts_id: fullDetails.parent_account_id || account.id || "",
        store_name: fullDetails.accDetails && fullDetails.accDetails.name,
        box_id: fullDetails.box_id || box.id || "",
        kiosk_name: fullDetails.boxDetails && fullDetails.boxDetails.name,
        consultation_notes: consultationNotes || "",
        is_releaseorder: true,
      };

      // Call batch API endpoint
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}batchsaveuserconsult`,
        {
          method: "POST",
          withCredentials: true,
          headers,
          body: JSON.stringify(consultationData),
        }
      );

      if (!response.ok) {
        let errorMessage = "Failed to save consultation.";
        try {
          const errorBody = await response.json();
          if (errorBody && errorBody.message) {
            errorMessage = errorBody.message;
          }
        } catch (parseError) {
          // Ignore parsing issues
        }
        throw new Error(errorMessage);
      }

      // Parse response
      const responseData = await response.json().catch(() => ({}));
      
      // Check if API returned success: false
      if (responseData && responseData.success === false) {
        const errorMessage = responseData.message || "Failed to save consultation.";
        throw new Error(errorMessage);
      }

      // Check for partial success (some succeeded, some failed)
      if (responseData && responseData.errors && responseData.errors.length > 0) {
        const errorMessages = responseData.errors.map(e => `${e.pickup_code}: ${e.error || e.message || "Unknown error"}`).join("; ");
        // Still throw error if there are failures, but log that some succeeded
        throw new Error(`Some consultations failed: ${errorMessages}`);
      }

      const pickupCodesStr = pickupCodes.join(", ");
      try {
        await writeAuditLog(
          `Batch consultation completed for pickup codes [${pickupCodesStr}].`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error(
          "Failed to write audit log for batch consultation:",
          auditError
        );
      }

      // Refresh popup data for all updated orders using batch API
      const orderIdsToRefresh = ordersToUpdate.map(o => o.orderId);
      const updatedFullDetailsMap = await this.refreshOrderDetailsBatch(orderIdsToRefresh, true);

      // Extract consultation notes from first order (they should be the same for all)
      let updatedConsultationNotes = consultationNotes || "";
      if (ordersToUpdate.length > 0) {
        const firstOrderId = ordersToUpdate[0].orderId;
        const firstOrderData = updatedFullDetailsMap[firstOrderId];
        if (firstOrderData && firstOrderData.consultationDetails && Array.isArray(firstOrderData.consultationDetails) && firstOrderData.consultationDetails.length > 0) {
          const latestConsultation = firstOrderData.consultationDetails[firstOrderData.consultationDetails.length - 1];
          updatedConsultationNotes = latestConsultation.consultation_notes || consultationNotes || "";
        }
      }

      this.component.setState({
        consultationNotes: updatedConsultationNotes,
      });

      if (this.component.socket) {
        this.component.socket.emit("updateConsole");
      }
    } catch (error) {
      console.error("Error saving batch consultation:", error);
      throw error;
    }
  }

  /**
   * Scan ID barcode and save customer collected details
   */
  async handleScanId(scannedBarcodeValue, orderIds = null) {
    const { popupData } = this.component.state;
    
    if (!scannedBarcodeValue || !scannedBarcodeValue.trim()) {
      throw new Error("Scanned barcode value is required.");
    }

    // If orderIds is provided (array), it's a batch operation from MultiPickupDialog
    if (Array.isArray(orderIds) && orderIds.length > 0) {
      await this.handleBatchScanId(orderIds, scannedBarcodeValue);
      return;
    }

    // Single order operation
    if (!popupData) {
      throw new Error("Order data is missing.");
    }

    const customer = popupData.customer || {};
    const account = popupData.account || popupData.accDetails || {};
    const box = popupData.box || popupData.boxDetails || {};

    const customerId = popupData.customer_id || customer.id || null;
    const boxId = popupData.box_id || box.id || null;
    const accountId = popupData.parent_account_id || account.id || null;
    const orderId = popupData.id || null;
    const kioskBoxAccountConnection = account.accountConnectionsId || null;

    if (!orderId) {
      throw new Error("Unable to determine order ID for ID scan.");
    }

    const IdObject = {
      customer_id: customerId,
      middle_name: null,
      box_id: boxId,
      account_id: accountId,
      orderIds: JSON.stringify([{ id: orderId }]),
      dlrawdata: scannedBarcodeValue.trim(),
      kioskBoxAccountConnection: kioskBoxAccountConnection,
      call_third_party_API: false,
      strictCheck: true,
    };

    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      const token =
        user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken;

      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}saveKioskCustomerCollectedDetail`,
        {
          method: "POST",
          withCredentials: true,
          headers,
          body: JSON.stringify(IdObject),
        }
      );

      if (!response.ok) {
        let errorMessage = "Failed to save scanned ID data.";
        try {
          const errorBody = await response.json();
          if (errorBody && errorBody.message) {
            errorMessage = errorBody.message;
          }
        } catch (jsonError) {
          try {
            const errorText = await response.text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (textError) {
            // Ignore parsing errors
          }
        }
        throw new Error(errorMessage);
      }

      try {
        await response.text();
      } catch (parseError) {
        // Ignore parsing errors
      }

      const pickupCode =
        popupData.pucode || popupData.pickup_code || popupData.id || "";
      
      try {
        await writeAuditLog(
          `ID barcode scanned and saved for counter order ${pickupCode}.`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error("Failed to write audit log for ID scan:", auditError);
      }

      await this.refreshPopupData();
      
      if (this.component.socket) {
        this.component.socket.emit("updateConsole");
      }
    } catch (error) {
      console.error("Error scanning ID:", error);
      throw error;
    }
  }

  /**
   * Batch scan ID for multiple orders
   * @param {Array<number|string>} orderIds - Array of order IDs to save ID scan for
   * @param {string} scannedBarcodeValue - Scanned barcode value
   */
  async handleBatchScanId(orderIds, scannedBarcodeValue) {
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      throw new Error("No orders provided for ID scan.");
    }

    const { patientOrders, orderFullDetailsMap } = this.component.state;
    
    if (!patientOrders || patientOrders.length === 0) {
      throw new Error("Order data is missing.");
    }

    // Get common data from first order
    const firstOrder = (patientOrders || []).find((o) => o && o.id === orderIds[0]);
    if (!firstOrder) {
      throw new Error("First order not found.");
    }

    const fullDetails = orderFullDetailsMap[firstOrder.id] || firstOrder;
    const customer = fullDetails.customer || {};
    const account = fullDetails.account || fullDetails.accDetails || {};
    const box = fullDetails.box || fullDetails.boxDetails || {};

    const customerId = fullDetails.customer_id || customer.id || null;
    const boxId = fullDetails.box_id || box.id || null;
    const accountId = fullDetails.parent_account_id || account.id || null;
    const kioskBoxAccountConnection = account.accountConnectionsId || null;

    if (!customerId || !boxId || !accountId) {
      throw new Error("Missing required order information for ID scan.");
    }

    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      const token =
        user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken;

      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // Prepare orderIds array in the format expected by API
      const orderIdsArray = orderIds.map(id => ({ id }));

      const IdObject = {
        customer_id: customerId,
        middle_name: null,
        box_id: boxId,
        account_id: accountId,
        orderIds: JSON.stringify(orderIdsArray),
        dlrawdata: scannedBarcodeValue.trim(),
        kioskBoxAccountConnection: kioskBoxAccountConnection,
        call_third_party_API: false,
        strictCheck: true,
      };

      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}saveKioskCustomerCollectedDetail`,
        {
          method: "POST",
          withCredentials: true,
          headers,
          body: JSON.stringify(IdObject),
        }
      );

      if (!response.ok) {
        let errorMessage = "Failed to save scanned ID data.";
        try {
          const errorBody = await response.json();
          if (errorBody && errorBody.message) {
            errorMessage = errorBody.message;
          }
        } catch (jsonError) {
          try {
            const errorText = await response.text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (textError) {
            // Ignore parsing errors
          }
        }
        throw new Error(errorMessage);
      }

      try {
        await response.json();
      } catch (parseError) {
        // Ignore parsing errors
      }

      const orderIdsStr = orderIds.join(", ");
      try {
        await writeAuditLog(
          `ID barcode scanned and saved for counter orders [${orderIdsStr}].`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error("Failed to write audit log for batch ID scan:", auditError);
      }

      // Refresh popup data for all updated orders using batch API
      await this.refreshOrderDetailsBatch(orderIds, true);

      if (this.component.socket) {
        this.component.socket.emit("updateConsole");
      }
    } catch (error) {
      console.error("Error scanning ID for batch orders:", error);
      throw error;
    }
  }

  /**
   * Set or unset payroll deduct (Pay Later) on a batch of orders.
   * Toggle direction: if any order already has is_payroll_deduct true → unset all; else set all.
   * @param {Array<number|string>} orderIds
   * @param {{ onSuccess?: (msg: string) => void }} options
   */
  async handlePayrollDeduct(orderIds, { onSuccess } = {}) {
    if (!Array.isArray(orderIds) || orderIds.length === 0) return;

    const { orderFullDetailsMap, patientOrders } = this.component.state;

    const user = JSON.parse(sessionStorage.getItem("user"));
    const token =
      user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.jwtToken;

    if (!token) throw new Error("Authentication token is missing.");

    // Toggle direction: any already set → unset all, otherwise set all
    const anyAlreadySet = orderIds.some(
      (id) =>
        (orderFullDetailsMap[id] && orderFullDetailsMap[id].is_payroll_deduct === true) ||
        ((patientOrders || []).find((o) => o && o.id === id) || {}).is_payroll_deduct === true
    );
    const nextValue = !anyAlreadySet;

    const payload = orderIds.map((id) => ({ id, is_payroll_deduct: nextValue }));

    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

    const response = await fetch(`${process.env.REACT_APP_BASEURL}setPayrollDeduct`, {
      method: "PUT",
      withCredentials: true,
      headers,
      body: JSON.stringify(payload),
    });

    const responseData = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error((responseData && responseData.message) || `HTTP error ${response.status}`);
    }

    if (responseData && responseData.success === false) {
      throw new Error((responseData && responseData.message) || "Failed to update Pay Later status.");
    }

    // Audit log
    try {
      const pucodes = orderIds
        .map((id) => {
          const o = (patientOrders || []).find((p) => p && p.id === id);
          return (o && (o.pucode || o.pickup_code)) || id;
        })
        .join(", ");
      await writeAuditLog(
        `Payroll deduction ${nextValue ? "set" : "unset"} for orders [${pucodes}].`,
        "Counter Operations"
      );
    } catch (auditError) {
      console.error("Failed to write audit log for payroll deduct:", auditError);
    }

    // Refresh orderFullDetailsMap from server (for dialog / completion; list uses websocket only)
    const updatedFullDetailsMap = await this.refreshOrderDetailsBatch(orderIds, false);

    // Update patientOrders from refreshed batch data so dialog sees new is_payroll_deduct / is_paid
    const currentPatientOrders = this.component.state.patientOrders || [];
    const updatedPatientOrders = currentPatientOrders.map((order) => {
      if (!order || !order.id || !orderIds.includes(order.id)) return order;
      const fullDetails = updatedFullDetailsMap[order.id];
      if (!fullDetails) return order;
      const newIsPaid = this.extractPaymentStatus(fullDetails);
      const amountData = fullDetails.amount;
      return {
        ...order,
        is_payroll_deduct: fullDetails.is_payroll_deduct === true,
        is_paid: newIsPaid,
        amount: amountData ? { ...amountData, isPaid: newIsPaid, is_paid: newIsPaid } : order.amount,
      };
    });

    // Recalculate completion status using refreshed full details and updated patient orders
    const { checkOrderCompletion } = require("../helpers/completionUtils");
    const updatedCompletionStatus = {};
    updatedPatientOrders.forEach((order) => {
      if (!order || !order.id) return;
      const fullDetails = updatedFullDetailsMap[order.id] || null;
      const bagScanStatus = (this.component.state.orderBagScanStatus || {})[order.id] || null;
      updatedCompletionStatus[order.id] = checkOrderCompletion(
        order,
        fullDetails,
        updatedPatientOrders,
        updatedFullDetailsMap,
        bagScanStatus
      );
    });

    this.component.setState({
      orderCompletionStatus: updatedCompletionStatus,
      patientOrders: updatedPatientOrders,
    });

    // Emit socket so the main counter list refreshes from websocket (Pay Later from socket only)
    if (this.component.socket) {
      this.component.socket.emit("updateConsole");
    }

    const message = nextValue
      ? "Pay Later option has been set successfully"
      : "Pay Later option has been removed";
    if (onSuccess) onSuccess(message);

    return nextValue;
  }

  /**
   * Process payment through M400 terminal (POS Bridge Service)
   * @param {Array<number|string>} orderIds - Array of order IDs to process payment for
   */
  async handleM400Payment(orderIds) {
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      throw new Error("No orders provided for M400 payment.");
    }

    const { patientOrders, orderFullDetailsMap } = this.component.state;
    
    if (!patientOrders || patientOrders.length === 0) {
      throw new Error("Order data is missing.");
    }

    // Calculate total amount and collect order details
    let totalAmount = 0;
    let hasValidOrders = false;
    const orderDetails = [];
    let clientId = null;
    let merchantId = null;
    let customerId = null;
    let boxId = null;

    for (const orderId of orderIds) {
      const order = (patientOrders || []).find((o) => o && o.id === orderId);
      if (!order) continue;

      const fullDetails = orderFullDetailsMap[orderId] || order;
      const orderAmount = calculateTotalDueAmount(fullDetails);
      
      // Skip orders with zero amount
      if (orderAmount <= 0) continue;
      
      totalAmount += orderAmount;
      hasValidOrders = true;
      
      // Collect order details
      orderDetails.push({
        orderId,
        amount: orderAmount,
        fullDetails,
      });

      // Get clientId, merchantId, and boxId from first order
      if (!clientId) {
        const account = fullDetails.account || fullDetails.accDetails || {};
        const box = fullDetails.box || fullDetails.boxDetails || {};
        
        // Try multiple ways to get clientId
        clientId = fullDetails.client_id || 
                  account.client_id ||
                  fullDetails.parent_account_id ||  // Use account ID as fallback
                  account.id ||                      // Use account ID as fallback
                  null;
        
        merchantId = fullDetails.merchant_id ||
                    account.merchant_id ||
                    null;
        
        customerId = fullDetails.customer_id ||
                    (fullDetails.customer && fullDetails.customer.id) ||
                    null;
        
        // Get boxId for routing to correct POS device
        boxId = fullDetails.box_id ||
                box.id ||
                null;
      }
    }

    if (!hasValidOrders) {
      throw new Error("No valid orders with payment amount found for M400 payment.");
    }

    // If clientId still not found, try to get it from user session
    if (!clientId) {
      try {
        const user = JSON.parse(sessionStorage.getItem("user"));
        if (user) {
          // Try to get clientId from user attributes or payload
          clientId = (user.attributes && user.attributes.client_id) || 
                    (user.payload && user.payload.client_id) ||
                    user.client_id ||
                    (user.signInUserSession && 
                     user.signInUserSession.idToken && 
                     user.signInUserSession.idToken.payload && 
                     user.signInUserSession.idToken.payload.client_id) ||
                    null;
        }
      } catch (e) {
        // Ignore errors when parsing user session
      }
    }

    if (!clientId) {
      throw new Error("Client ID is required for payment processing. Please ensure order has client information.");
    }

    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      const token =
        user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken;

      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // Step 1: Initiate payment request (stage transaction)
      const initiateResponse = await fetch(
        `${process.env.REACT_APP_BASEURL}api/pos-payments/initiate`,
        {
          method: "POST",
          withCredentials: true,
          headers,
          body: JSON.stringify({
            clientId,
            merchantId,
            amount: Number(totalAmount.toFixed(2)),
            transactionType: "SALE",
            orderId: orderIds.length === 1 ? orderIds[0] : null, // Single order ID or null for batch
            customerId,
            boxId: boxId, // Include boxId for routing to correct POS device
            posDeviceInfo: {
              orderIds: orderIds,
              orderCount: orderIds.length,
            },
          }),
        }
      );

      if (!initiateResponse.ok) {
        let errorMessage = "Failed to initiate payment request.";
        try {
          const errorBody = await initiateResponse.json();
          if (errorBody && errorBody.message) {
            errorMessage = errorBody.message;
          }
        } catch (parseError) {
          // Ignore parsing issues
        }
        throw new Error(errorMessage);
      }

      const initiateData = await initiateResponse.json();
      const requestId = initiateData.data && initiateData.data.requestId;

      if (!requestId) {
        throw new Error("Payment request initiated but no request ID returned.");
      }

      // Step 2: Poll for payment status
      const maxPollAttempts = 120; // 2 minutes (120 * 1 second)
      const pollInterval = 1000; // 1 second
      const stagedTimeout = 15; // Stop polling if status stays "staged" for 15 seconds
      let pollAttempts = 0;
      let paymentCompleted = false;
      let paymentStatus = null;
      let stagedStartTime = null;
      let lastStatus = null;

      while (pollAttempts < maxPollAttempts && !paymentCompleted) {
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        pollAttempts++;

        try {
          const statusResponse = await fetch(
            `${process.env.REACT_APP_BASEURL}api/pos-payments/status/${requestId}`,
            {
              method: "GET",
              withCredentials: true,
              headers,
            }
          );

          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            paymentStatus = statusData.data;

            if (paymentStatus && paymentStatus.status === "completed") {
              paymentCompleted = true;
              break;
            } else if (paymentStatus && paymentStatus.status === "failed") {
              throw new Error(
                paymentStatus.errorMessage || "Payment processing failed."
              );
            }

            // Track if status has changed from "staged"
            const currentStatus = paymentStatus && paymentStatus.status;
            if (currentStatus === "staged") {
              if (lastStatus !== "staged") {
                // Status just became "staged" or is still "staged"
                stagedStartTime = stagedStartTime || Date.now();
              }
              
              // If status has been "staged" for too long, stop polling
              // This means the local service hasn't picked up the payment request
              if (stagedStartTime && (Date.now() - stagedStartTime) > (stagedTimeout * 1000)) {
                console.warn("Payment request is staged but local service hasn't processed it yet.");
                // Don't throw error, just stop polling - payment was initiated successfully
                // The status will be checked when user refreshes or manually checks
                break;
              }
            } else if (currentStatus && currentStatus !== "staged") {
              // Status changed from "staged" to something else (initiated, processing, etc.)
              // Reset staged timer and continue polling
              stagedStartTime = null;
            }
            
            lastStatus = currentStatus;
            // Continue polling if status is still "staged", "pending", "initiated", or "processing"
          }
        } catch (statusError) {
          // Continue polling on error (might be temporary)
          console.warn(`Error checking payment status (attempt ${pollAttempts}):`, statusError);
        }
      }

      // Only throw timeout error if we were actively waiting for completion
      // If status is still "staged", it means the request was initiated but not yet processed
      if (!paymentCompleted && paymentStatus && paymentStatus.status === "staged") {
        // Payment request was initiated successfully but local service hasn't processed it yet
        // This is not an error - the payment will be processed when the local service picks it up
        console.log("Payment request initiated. Waiting for local POS service to process.");
        return {
          success: true,
          requestId: requestId,
          status: "staged",
          message: "Payment request initiated. Waiting for POS device to process payment.",
        };
      } else if (!paymentCompleted) {
        throw new Error(
          "Payment processing timed out. Please check the POS device or try again."
        );
      }

      // Step 3: Payment completed - refresh order data
      const updatedFullDetailsMap = await this.refreshOrderDetailsBatch(orderIds, false);

      // Update patientOrders array with new payment status
      const updatedPatientOrders = patientOrders.map((order) => {
        if (!order || !order.id || !orderIds.includes(order.id)) return order;
        
        const fullDetails = updatedFullDetailsMap[order.id];
        if (!fullDetails) return order;
        
        const newIsPaid = this.extractPaymentStatus(fullDetails);
        const amountData = fullDetails.amount;
        
        return {
          ...order,
          is_paid: newIsPaid,
          amount: amountData ? { ...amountData, isPaid: newIsPaid, is_paid: newIsPaid } : order.amount,
        };
      });

      // Recalculate completion status for all patient orders
      const { checkOrderCompletion } = require("../helpers/completionUtils");
      const updatedCompletionStatus = {};
      updatedPatientOrders.forEach((order) => {
        if (!order || !order.id) return;
        const fullDetails = updatedFullDetailsMap[order.id] || null;
        const bagScanStatus = this.component.state.orderBagScanStatus[order.id] || null;
        updatedCompletionStatus[order.id] = checkOrderCompletion(
          order,
          fullDetails,
          updatedPatientOrders,
          updatedFullDetailsMap,
          bagScanStatus
        );
      });

      this.component.setState({
        orderCompletionStatus: updatedCompletionStatus,
        orderFullDetailsMap: updatedFullDetailsMap,
        patientOrders: updatedPatientOrders,
      });

      // Step 4: Audit log
      const orderIdsStr = orderIds.join(", ");
      try {
        await writeAuditLog(
          `M400 payment processed successfully for orders [${orderIdsStr}]. Amount: $${totalAmount.toFixed(2)}, Request ID: ${requestId}.`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error("Failed to write audit log for M400 payment:", auditError);
      }

      // Step 5: Emit socket update
      if (this.component.socket) {
        this.component.socket.emit("updateConsole");
      }

      // Return success result
      return {
        success: true,
        requestId: requestId,
        status: "completed",
        message: "Payment processed successfully.",
        amount: totalAmount,
        orderIds,
      };
    } catch (error) {
      console.error("Error processing M400 payment:", error);
      
      // Write error audit log
      try {
        const orderIdsStr = orderIds.join(", ");
        await writeAuditLog(
          `M400 payment failed for orders [${orderIdsStr}]. Error: ${error.message || "Unknown error"}.`,
          "Counter Operations"
        );
      } catch (auditError) {
        console.error("Failed to write audit log for M400 payment error:", auditError);
      }
      
      throw error;
    }
  }
}

export default OrderService;




// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/services/OrderService.js